import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO, { createWebPageSchema, createBreadcrumbSchema, createFAQPageSchema, createOrganizationSchema } from '../../components/feature/SEO';
import WhatsAppButton from '../../components/base/WhatsAppButton';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../utils/siteSettings';

const REVENUE_HERO_DEFAULT = 'https://public.readdy.ai/ai/img_res/1877fba79afcb4443e43290bc7683528.jpg';

const RevenueSharing = () => {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://unqcode.com';
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const steps = [
    { icon: 'ri-discuss-line', titleKey: 'rev.step1Title', descKey: 'rev.step1Desc' },
    { icon: 'ri-file-text-line', titleKey: 'rev.step2Title', descKey: 'rev.step2Desc' },
    { icon: 'ri-rocket-line', titleKey: 'rev.step3Title', descKey: 'rev.step3Desc' },
    { icon: 'ri-pie-chart-line', titleKey: 'rev.step4Title', descKey: 'rev.step4Desc' },
  ];

  const benefits = [
    { icon: 'ri-money-dollar-circle-line', titleKey: 'rev.ben1Title', descKey: 'rev.ben1Desc' },
    { icon: 'ri-bar-chart-grouped-line', titleKey: 'rev.ben2Title', descKey: 'rev.ben2Desc' },
    { icon: 'ri-shield-star-line', titleKey: 'rev.ben3Title', descKey: 'rev.ben3Desc' },
    { icon: 'ri-hand-coin-line', titleKey: 'rev.ben4Title', descKey: 'rev.ben4Desc' },
    { icon: 'ri-customer-service-2-line', titleKey: 'rev.ben5Title', descKey: 'rev.ben5Desc' },
    { icon: 'ri-line-chart-line', titleKey: 'rev.ben6Title', descKey: 'rev.ben6Desc' },
  ];

  const faqs = [
    { qKey: 'rev.faq1Q', aKey: 'rev.faq1A' },
    { qKey: 'rev.faq2Q', aKey: 'rev.faq2A' },
    { qKey: 'rev.faq3Q', aKey: 'rev.faq3A' },
    { qKey: 'rev.faq4Q', aKey: 'rev.faq4A' },
    { qKey: 'rev.faq5Q', aKey: 'rev.faq5A' },
    { qKey: 'rev.faq6Q', aKey: 'rev.faq6A' },
  ];

  const modelStats = [
    { valueKey: 'rev.stat1Value', labelKey: 'rev.stat1Label' },
    { valueKey: 'rev.stat2Value', labelKey: 'rev.stat2Label' },
    { valueKey: 'rev.stat3Value', labelKey: 'rev.stat3Label' },
    { valueKey: 'rev.stat4Value', labelKey: 'rev.stat4Label' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={t('seo.revenueTitle')}
        description={t('seo.revenueDesc')}
        keywords="revenue sharing, partnership model, zero upfront investment, digital agency partnership, Saudi Arabia business partnership, تقاسم الأرباح, نموذج الشراكة"
        canonical={`${siteUrl}/revenue-sharing`}
        ogType="website"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            createWebPageSchema(
              t('seo.revenueTitle', 'Revenue Sharing Partnership Model | Unique Code - الكود المميز'),
              t('seo.revenueDesc', 'Zero upfront investment partnership model. We grow together - you only pay when you succeed. Revenue sharing digital services in Saudi Arabia.'),
              '/revenue-sharing'
            ),
            createOrganizationSchema(),
            createBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Revenue Sharing', url: '/revenue-sharing' },
            ]),
            {
              '@type': 'Service',
              '@id': `${siteUrl}/revenue-sharing#service`,
              name: 'Revenue Sharing Partnership Model',
              alternateName: 'نموذج تقاسم الأرباح',
              description: 'Zero upfront investment digital marketing and growth partnership. Unique Code invests in your business growth and earns a percentage of new revenue generated — no large retainers required.',
              serviceType: 'Revenue Sharing Digital Marketing',
              provider: {
                '@id': `${siteUrl}/#organization`,
              },
              areaServed: [
                { '@type': 'Country', name: 'Saudi Arabia' },
                { '@type': 'Country', name: 'United Arab Emirates' },
                { '@type': 'AdministrativeArea', name: 'GCC' },
              ],
              offers: {
                '@type': 'Offer',
                description: 'Performance-based revenue share — 15% to 30% of new revenue generated. Minimal onboarding fee only.',
                priceCurrency: 'SAR',
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  description: 'Performance-based: 15%–30% of generated revenue',
                },
              },
              hasOfferCatalog: {
                '@type': 'OfferCatalog',
                name: 'Revenue Sharing Services',
                itemListElement: [
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Digital Marketing & SEO' } },
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Paid Advertising (Google, Meta)' } },
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Social Media Management' } },
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Content Marketing' } },
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Conversion Rate Optimization' } },
                  { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Analytics & Reporting' } },
                ],
              },
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: '4.9',
                reviewCount: '120',
                bestRating: '5',
                worstRating: '1',
              },
            },
            createFAQPageSchema([
              {
                question: 'What percentage of revenue does Unique Code take?',
                answer: 'It varies by industry and campaign scale, but typically ranges between 15%–30% of new revenue generated from our efforts. This is negotiated transparently upfront.',
              },
              {
                question: 'Is there any upfront cost at all?',
                answer: 'We require a minimal onboarding fee to cover setup and strategy — far below typical agency retainers. After that, it\'s purely performance-based.',
              },
              {
                question: 'How long is the minimum contract?',
                answer: 'We recommend a minimum 6-month commitment to see meaningful results. Most clients stay long-term once they see the compounding growth.',
              },
              {
                question: 'Which industries does this work for?',
                answer: 'We\'ve successfully run this model in e-commerce, real estate, SaaS, healthcare, education, hospitality, and more. Book a call to assess your industry fit.',
              },
              {
                question: 'How is revenue tracked and verified?',
                answer: 'We integrate directly with your CRM, analytics tools, and payment systems to ensure transparent, auditable tracking of every lead and conversion we generate.',
              },
              {
                question: 'Can I switch to a standard retainer later?',
                answer: 'Absolutely. Many clients start with revenue sharing and transition to a hybrid or retainer model once trust is established and budgets grow.',
              },
            ]),
          ],
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img src={getImageUrl('hero-revenue', REVENUE_HERO_DEFAULT)} alt="Revenue Sharing" className="w-full h-full object-cover object-top opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/40"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-red/20 border border-brand-red/30 rounded-full px-4 py-2 mb-8">
              <i className="ri-exchange-dollar-line text-brand-red"></i>
              <span className="text-white/80 text-sm font-medium tracking-wide">{t('rev.heroBadge')}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
              {t('rev.heroTitle1')} <span className="text-brand-red">{t('rev.heroTitle2')}</span>
              <span className="block text-white/60 text-3xl md:text-4xl font-light mt-2">{t('rev.heroSubtitle')}</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed mb-10 max-w-xl">{t('rev.heroDesc')}</p>
            <Link to="/contact">
              <button className="px-8 py-4 bg-brand-red text-white font-bold rounded-xl hover:bg-brand-red-dark transition-all hover:scale-105 whitespace-nowrap cursor-pointer flex items-center gap-3">
                {t('rev.exploreBtn')} <i className="ri-arrow-right-line rtl:rotate-180"></i>
              </button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
      </section>

      {/* What is it */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('rev.modelLabel')}</p>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">{t('rev.modelTitle')}</h2>
              <p className="text-gray-500 text-base leading-relaxed mb-5">{t('rev.modelDesc1')}</p>
              <p className="text-gray-500 text-base leading-relaxed mb-8">{t('rev.modelDesc2')}</p>
              <div className="grid grid-cols-2 gap-5">
                {modelStats.map((stat) => (
                  <div key={stat.labelKey} className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                    <div className="text-3xl font-black text-gray-900 mb-1">{t(stat.valueKey)}</div>
                    <div className="text-gray-500 text-sm">{t(stat.labelKey)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden h-[480px]">
              <img src="https://readdy.ai/api/search-image?query=two%20confident%20business%20partners%20shaking%20hands%20in%20modern%20dark%20office%20after%20successful%20strategic%20partnership%20deal%2C%20professionals%20in%20front%20of%20financial%20growth%20charts%20and%20digital%20screens%2C%20premium%20corporate%20photography%20with%20dramatic%20warm%20accent%20lighting%2C%20bokeh%20background%20effect&width=700&height=700&seq=rev-partner02&orientation=squarish" alt="Partnership" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('rev.processLabel')}</p>
            <h2 className="text-4xl font-black text-white">{t('rev.processTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={step.titleKey} className="relative text-center">
                <div className="relative inline-flex mb-6">
                  <div className="w-24 h-24 flex items-center justify-center bg-gray-800 border-2 border-brand-red rounded-2xl">
                    <i className={`${step.icon} text-brand-red text-3xl`}></i>
                  </div>
                  <div className="absolute -top-3 -right-3 rtl:-right-auto rtl:-left-3 w-8 h-8 flex items-center justify-center bg-brand-red rounded-full text-white text-xs font-black">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                </div>
                <h3 className="font-bold text-white text-lg mb-3">{t(step.titleKey)}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{t(step.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('rev.benefitsLabel')}</p>
            <h2 className="text-4xl font-black text-gray-900">{t('rev.benefitsTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b) => (
              <div key={b.titleKey} className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-brand-red/30 hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-red/10 group-hover:bg-brand-red rounded-xl mb-5 transition-colors duration-300">
                  <i className={`${b.icon} text-brand-red group-hover:text-white text-xl transition-colors duration-300`}></i>
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{t(b.titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(b.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gray-50/50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('rev.faqLabel')}</p>
            <h2 className="text-4xl font-black text-gray-900">{t('rev.faqTitle')}</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-bold text-gray-900 text-base pe-4">{t(faq.qKey)}</span>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0 transition-all duration-200 ${openFaq === i ? 'bg-brand-red border-brand-red text-white' : 'border-gray-200 text-gray-400'}`}>
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-sm`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-500 text-sm leading-relaxed">{t(faq.aKey)}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-red">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5">{t('rev.ctaTitle')}</h2>
          <p className="text-white/80 text-lg max-w-xl mx-auto mb-10">{t('rev.ctaDesc')}</p>
          <Link to="/partnership">
            <button className="px-10 py-4 bg-white text-brand-red font-black text-lg rounded-xl hover:bg-gray-50 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
              {t('rev.ctaBtn')}
            </button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default RevenueSharing;
